import { useCallback, useEffect, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchUnreadCount, type AnchorUnread } from '../../api/comments';
import { ConversationModal, type PostPreview, type ResolvedUser } from './ConversationModal';
import { PostReactionButton } from './PostReactionButton';
import styles from './FeedActionBar.module.css';

// =============================================================================
// FeedActionBar (DEV-720). The Facebook-style action row at the BOTTOM of a
// feed / community-feed / Journal card: react (heart + emoji picker) + comment
// + share, with live counts and reaction faces. Opens the standard
// ConversationModal. Reaction control is the shared PostReactionButton so every
// surface behaves identically.
// =============================================================================

interface FeedActionBarProps {
  anchorType: string;
  anchorId: string;
  post?: PostPreview;
  resolveUser?: (guid: string) => ResolvedUser | undefined;
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
}

export function FeedActionBar({ anchorType, anchorId, post, resolveUser }: FeedActionBarProps) {
  const { t } = useAppTranslation('common');
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<AnchorUnread | null>(null);

  const refresh = useCallback(() => {
    fetchUnreadCount(anchorType, anchorId).then(setSummary).catch(() => {});
  }, [anchorType, anchorId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const reactions = summary?.postReactions ?? [];
  const vocab = summary?.reactionVocab ?? [];
  const faces = reactions.filter((r) => r.count > 0).map((r) => r.reactionType);
  const commentCount = summary?.totalCount ?? 0;

  const onShare = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ url }).catch(() => {});
    else navigator.clipboard?.writeText(url).catch(() => {});
  };

  const close = () => {
    setOpen(false);
    refresh();
  };

  return (
    <>
      <div className={styles.bar} onClick={(e) => e.stopPropagation()}>
        <PostReactionButton
          anchorType={anchorType}
          anchorId={anchorId}
          reactions={reactions}
          vocab={vocab}
          onChanged={refresh}
        />
        <button type="button" className={styles.btn} onClick={() => setOpen(true)}>
          <i className="ti ti-message-circle" aria-hidden="true" />
          {commentCount > 0 && <span>{formatCount(commentCount)}</span>}
        </button>
        <button type="button" className={styles.btn} aria-label={t('comments.share')} onClick={onShare}>
          <i className="ti ti-share-3" aria-hidden="true" />
        </button>
        {faces.length > 0 && <span className={styles.faces}>{faces.join('')}</span>}
      </div>

      <ConversationModal
        anchorType={anchorType}
        anchorId={anchorId}
        open={open}
        onClose={close}
        post={post}
        resolveUser={resolveUser}
        layout={post?.imageUrl ? 'theater' : 'modal'}
      />
    </>
  );
}

export default FeedActionBar;
